import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  Drawer,
  Snackbar,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import useApi from '../../../utils/useApi';
import { formattedCurrency } from '../../../utils/utils';
import SaleMobileQuantityModal from './SaleMobileQuantityModal';
import './saleMobile.css';

const SaleMobileCatalog = ({
  listItens = [],
  onSelectItem,
  onEditItem,
  onRemoveItem,
  onBarCodeSubmit,
}) => {
  const lastClickTimeRef = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL'); // 'ALL' | 'PRODUCTS' | 'SERVICES' | category_id
  const [visibleLimit, setVisibleLimit] = useState(50); // Paginação inicial de 50 itens
  const [openCategorySheet, setOpenCategorySheet] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [quantityModalItem, setQuantityModalItem] = useState(null);

  // APIs para carregar dados
  const [productsLoad, productsInfo] = useApi({
    method: 'get',
    url: '/products',
    params: { lacking: 0 },
  });

  const [servicesLoad, servicesInfo] = useApi({
    method: 'get',
    url: '/services',
    params: { isActive: 1 },
  });

  const [prodCategoriesLoad, prodCategoriesInfo] = useApi({
    method: 'get',
    url: '/products/categories',
    params: { isActive: 1 },
  });

  const [servCategoriesLoad, servCategoriesInfo] = useApi({
    method: 'get',
    url: '/services/categories',
    params: { isActive: 1 },
  });

  useEffect(() => {
    productsLoad();
    servicesLoad();
    prodCategoriesLoad();
    servCategoriesLoad();
  }, []);

  // Helper seguro para extrair o ID da categoria de produtos/serviços
  const getItemCategoryId = useCallback((item) => {
    if (!item || item.category === null || item.category === undefined) return null;
    if (typeof item.category === 'object') {
      return item.category.id !== undefined ? String(item.category.id) : null;
    }
    return String(item.category);
  }, []);

  // Mapa de nomes de categorias para exibição
  const categoryNamesMap = useMemo(() => {
    const map = new Map();
    (prodCategoriesInfo.data || []).forEach((c) => map.set(String(c.id), c.name));
    (servCategoriesInfo.data || []).forEach((c) => map.set(String(c.id), c.name));
    return map;
  }, [prodCategoriesInfo.data, servCategoriesInfo.data]);

  // Mapeamento dos produtos e serviços normalizados
  const allCatalogItems = useMemo(() => {
    const prods = (productsInfo.data || []).map((p) => {
      const catId = getItemCategoryId(p);
      return {
        ...p,
        typeItem: 'product',
        itemKey: `product-${p.id}`,
        displayCategory: p.category?.name || categoryNamesMap.get(catId) || 'Produto',
        numericValue: Number(p.value || 0),
        resolvedCatId: catId,
      };
    });

    const servs = (servicesInfo.data || []).map((s) => {
      const catId = getItemCategoryId(s);
      return {
        ...s,
        typeItem: 'service',
        itemKey: `service-${s.id}`,
        displayCategory: s.category?.name || categoryNamesMap.get(catId) || 'Serviço',
        numericValue: Number(s.value || 0),
        resolvedCatId: catId,
      };
    });

    return [...prods, ...servs];
  }, [productsInfo.data, servicesInfo.data, categoryNamesMap, getItemCategoryId]);

  // Lista consolidada de categorias com contadores exatos
  const categoriesList = useMemo(() => {
    const prods = productsInfo.data || [];
    const servs = servicesInfo.data || [];

    const prodCats = (prodCategoriesInfo.data || []).map((c) => {
      const count = prods.filter((p) => getItemCategoryId(p) === String(c.id)).length;
      return {
        id: `prod-cat-${c.id}`,
        rawId: String(c.id),
        name: c.name,
        type: 'product',
        count,
      };
    });

    const servCats = (servCategoriesInfo.data || []).map((c) => {
      const count = servs.filter((s) => getItemCategoryId(s) === String(c.id)).length;
      return {
        id: `serv-cat-${c.id}`,
        rawId: String(c.id),
        name: c.name,
        type: 'service',
        count,
      };
    });

    return {
      allCategories: [...prodCats, ...servCats],
      prodCount: prods.length,
      servCount: servs.length,
      totalCount: prods.length + servs.length,
    };
  }, [productsInfo.data, servicesInfo.data, prodCategoriesInfo.data, servCategoriesInfo.data, getItemCategoryId]);

  // Nome da categoria selecionada para o botão
  const selectedCategoryLabel = useMemo(() => {
    if (selectedCategory === 'ALL') return null;
    const found = categoriesList.allCategories.find((c) => c.id === selectedCategory);
    return found ? found.name : null;
  }, [selectedCategory, categoriesList.allCategories]);

  // Helper para normalizar acentuação, maiúsculas/minúsculas e pontuação
  const normalizeText = (text) => {
    if (!text) return '';
    return String(text)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  };

  // Mapa rápido de itens que estão no carrinho para saber quantidade
  const cartMap = useMemo(() => {
    const map = new Map();
    listItens.forEach((cartItem, idx) => {
      const key = `${cartItem.typeItem}-${cartItem.id}`;
      map.set(key, {
        index: idx,
        amount: Number(cartItem.amount || 0),
      });
    });
    return map;
  }, [listItens]);

  // Lista filtrada para a gaveta de categorias
  const filteredCategorySheetList = useMemo(() => {
    if (!categorySearch.trim()) return categoriesList.allCategories;
    const term = normalizeText(categorySearch);
    return categoriesList.allCategories.filter((c) => normalizeText(c.name).includes(term));
  }, [categoriesList.allCategories, categorySearch]);

  // Filtro de itens conforme busca e categoria selecionada
  const filteredItems = useMemo(() => {
    let items = allCatalogItems;
    const cleanSearch = searchTerm.trim();

    // Filtro por 'SELECTED'
    if (selectedCategory === 'SELECTED') {
      const catalogMatches = allCatalogItems.filter((i) => {
        const itemKey = i.itemKey || `${i.typeItem}-${i.id}`;
        return (cartMap.get(itemKey)?.amount || 0) > 0;
      });

      const matchedKeys = new Set(catalogMatches.map((i) => i.itemKey || `${i.typeItem}-${i.id}`));
      const additionalFromCart = listItens
        .filter((c) => !matchedKeys.has(`${c.typeItem || 'product'}-${c.id}`))
        .map((c) => ({
          ...c,
          typeItem: c.typeItem || 'product',
          itemKey: `${c.typeItem || 'product'}-${c.id}`,
          displayCategory: c.category?.name || (c.typeItem === 'service' ? 'Serviço' : 'Produto'),
          numericValue: Number(c.valueUn || c.value || 0),
          resolvedCatId: '0',
        }));

      items = [...catalogMatches, ...additionalFromCart];
    } else if (selectedCategory.startsWith('prod-cat-')) {
      const catId = selectedCategory.replace('prod-cat-', '');
      items = items.filter(
        (i) => i.typeItem === 'product' && i.resolvedCatId === String(catId)
      );
    } else if (selectedCategory.startsWith('serv-cat-')) {
      const catId = selectedCategory.replace('serv-cat-', '');
      items = items.filter(
        (i) => i.typeItem === 'service' && i.resolvedCatId === String(catId)
      );
    }

    // Filtro por texto sem sensibilidade a acentos (ex: "Agua" acha "Água")
    if (cleanSearch) {
      const normalizedSearch = normalizeText(cleanSearch);
      items = items.filter((i) => {
        const nameMatch = normalizeText(i.name).includes(normalizedSearch);
        const codeMatch = normalizeText(i.barCode).includes(normalizedSearch);
        const idMatch = String(i.id).includes(normalizedSearch);
        const catMatch = normalizeText(i.displayCategory).includes(normalizedSearch);
        return nameMatch || codeMatch || idMatch || catMatch;
      });
    }

    // Exibir no topo da lista os itens que já estão selecionados
    return [...items].sort((a, b) => {
      const aKey = a.itemKey || `${a.typeItem}-${a.id}`;
      const bKey = b.itemKey || `${b.typeItem}-${b.id}`;
      const aQty = cartMap.get(aKey)?.amount || 0;
      const bQty = cartMap.get(bKey)?.amount || 0;
      if (aQty > 0 && bQty === 0) return -1;
      if (aQty === 0 && bQty > 0) return 1;
      return 0;
    });
  }, [allCatalogItems, selectedCategory, searchTerm, cartMap]);

  // Reseta a paginação ao mudar busca ou categoria
  useEffect(() => {
    setVisibleLimit(50);
  }, [searchTerm, selectedCategory]);

  // Itens visíveis fatiados para performance (Infinite Scroll)
  const visibleItems = useMemo(() => {
    return filteredItems.slice(0, visibleLimit);
  }, [filteredItems, visibleLimit]);

  // Manipulador de Scroll Infinito (+50 itens)
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      setVisibleLimit((prev) => {
        if (prev < filteredItems.length) {
          return Math.min(prev + 50, filteredItems.length);
        }
        return prev;
      });
    }
  };

  const [stockAlert, setStockAlert] = useState(null);

  const handleOpenQuantityModal = (item, cartEntry = null) => {
    setQuantityModalItem({
      item,
      currentAmount: cartEntry ? cartEntry.amount : 1,
      cartIndex: cartEntry !== null && cartEntry !== undefined ? cartEntry.index : null,
      isNew: !cartEntry,
    });
  };

  const handleSaveQuantity = (newQty) => {
    if (!quantityModalItem) return;
    const { item, cartIndex, isNew } = quantityModalItem;
    if (isNew || cartIndex === null || cartIndex === undefined) {
      const maxStock = item.amount !== undefined && item.amount !== null ? Number(item.amount) : Infinity;
      if (item.typeItem === 'product' && maxStock <= 0) {
        setStockAlert(`O item "${item.name}" está esgotado no estoque!`);
        return;
      }
      if (onSelectItem) {
        onSelectItem({
          id: item.id,
          amountSelected: newQty,
          name: item.name,
          value: item.numericValue,
          barCode: item.barCode,
          typeItem: item.typeItem,
          category: item.category,
        });
      }
    } else {
      if (onEditItem) {
        onEditItem(cartIndex, newQty);
      }
    }
  };

  const handleRemoveFromModal = () => {
    if (!quantityModalItem) return;
    const { cartIndex } = quantityModalItem;
    if (cartIndex !== null && cartIndex !== undefined && onRemoveItem) {
      onRemoveItem(cartIndex);
    }
  };

  const handleAddClick = (item) => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 350) return;
    lastClickTimeRef.current = now;

    const maxStock = item.amount !== undefined && item.amount !== null ? Number(item.amount) : Infinity;
    if (item.typeItem === 'product' && maxStock <= 0) {
      setStockAlert(`O item "${item.name}" está esgotado no estoque!`);
      return;
    }
    if (onSelectItem) {
      onSelectItem({
        id: item.id,
        amountSelected: 1,
        name: item.name,
        value: item.numericValue,
        barCode: item.barCode,
        typeItem: item.typeItem,
        category: item.category,
      });
    }
  };

  const handleIncrement = (item, cartEntry) => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 350) return;
    lastClickTimeRef.current = now;

    const maxStock = item.amount !== undefined && item.amount !== null ? Number(item.amount) : Infinity;
    if (item.typeItem === 'product' && cartEntry.amount >= maxStock) {
      setStockAlert(`Quantidade máxima atingida! Existem apenas ${maxStock} unidades de "${item.name}" em estoque.`);
      return;
    }
    if (onEditItem) {
      onEditItem(cartEntry.index, Math.round((cartEntry.amount + 1) * 1000) / 1000);
    }
  };

  const handleDecrement = (item, cartEntry) => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 350) return;
    lastClickTimeRef.current = now;

    if (cartEntry.amount <= 0.1) {
      if (onRemoveItem) {
        onRemoveItem(cartEntry.index);
      }
    } else if (cartEntry.amount < 1) {
      const next = Math.max(0.001, Math.round((cartEntry.amount - 0.1) * 1000) / 1000);
      if (next <= 0.001) {
        if (onRemoveItem) onRemoveItem(cartEntry.index);
      } else if (onEditItem) {
        onEditItem(cartEntry.index, next);
      }
    } else if (cartEntry.amount === 1) {
      if (onRemoveItem) {
        onRemoveItem(cartEntry.index);
      }
    } else if (onEditItem) {
      onEditItem(cartEntry.index, Math.round((cartEntry.amount - 1) * 1000) / 1000);
    }
  };

  const handleSearchSubmit = () => {
    const raw = searchTerm.trim();
    if (!raw) return;

    let multiplier = 1;
    let code = raw;

    if (raw.includes('*')) {
      const parts = raw.split('*');
      if (parts.length === 2) {
        const parsedM = parseFloat(parts[0].replace(',', '.'));
        if (!isNaN(parsedM) && parsedM > 0) {
          multiplier = parsedM;
          code = parts[1].trim();
        }
      }
    }

    const normalizedCode = normalizeText(code);
    const exactMatch = allCatalogItems.find(
      (i) =>
        String(i.id) === code ||
        (i.barCode && String(i.barCode).trim().toLowerCase() === code.toLowerCase()) ||
        normalizeText(i.name) === normalizedCode
    );

    if (exactMatch) {
      const maxStock = exactMatch.amount !== undefined && exactMatch.amount !== null ? Number(exactMatch.amount) : Infinity;
      if (exactMatch.typeItem === 'product' && maxStock <= 0) {
        setStockAlert(`O item "${exactMatch.name}" está esgotado no estoque!`);
        return;
      }
      if (onSelectItem) {
        onSelectItem({
          id: exactMatch.id,
          amountSelected: multiplier,
          name: exactMatch.name,
          value: exactMatch.numericValue,
          barCode: exactMatch.barCode,
          typeItem: exactMatch.typeItem,
          category: exactMatch.category,
        });
        setSearchTerm('');
      }
    } else if (onBarCodeSubmit) {
      onBarCodeSubmit(raw);
    }
  };

  const isLoading = productsInfo.loading || servicesInfo.loading;

  return (
    <>
      {/* Header com Campo de Busca Instantânea */}
      <div className="sale-mobile-top-section">
        <div className="sale-mobile-search-bar">
          <SearchIcon style={{ color: '#64748b', fontSize: 20, marginRight: 4 }} />
          <input
            type="text"
            className="sale-mobile-search-input"
            placeholder="Buscar produto, serviço ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearchSubmit();
              }
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={() => setSearchTerm('')}
              style={{ padding: 2 }}
            >
              <ClearIcon style={{ fontSize: 16, color: '#64748b' }} />
            </IconButton>
          )}
        </div>

        {/* Barra de Filtros: Todas, Selecionados e Seletor de Categoria */}
        <div className="sale-mobile-filters-bar">
          <div
            className={`sale-mobile-cat-pill ${selectedCategory === 'ALL' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('ALL')}
          >
            Todos ({categoriesList.totalCount})
          </div>

          <div
            className={`sale-mobile-cat-pill ${selectedCategory === 'SELECTED' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('SELECTED')}
          >
            Selecionados ({listItens.length})
          </div>

          {/* Botão Seletor de Categorias (Abre Bottom Sheet) */}
          <div
            className={`sale-mobile-cat-trigger-btn ${selectedCategory !== 'ALL' && selectedCategory !== 'SELECTED' ? 'has-selected' : ''}`}
            onClick={() => setOpenCategorySheet(true)}
          >
            <LocalOfferIcon style={{ fontSize: 14 }} />
            <span>{selectedCategory !== 'SELECTED' ? (selectedCategoryLabel || 'Categorias') : 'Categorias'}</span>
            {selectedCategoryLabel && selectedCategory !== 'SELECTED' ? (
              <CloseIcon
                style={{ fontSize: 14, marginLeft: 4 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCategory('ALL');
                }}
              />
            ) : (
              <ExpandMoreIcon style={{ fontSize: 16 }} />
            )}
          </div>
        </div>
      </div>

      {/* Bottom Sheet Modal de Categorias */}
      <Drawer
        anchor="bottom"
        open={openCategorySheet}
        onClose={() => {
          setOpenCategorySheet(false);
          setCategorySearch('');
        }}
        PaperProps={{
          style: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '80vh',
            backgroundColor: '#ffffff',
          },
        }}
      >
        <div className="sale-mobile-drawer-content" style={{ maxHeight: '80vh' }}>
          <div className="sale-mobile-drawer-header">
            <div className="sale-mobile-drawer-title">
              <LocalOfferIcon style={{ color: '#17497f', fontSize: 20 }} />
              <span>Categorias</span>
            </div>
            <IconButton
              size="small"
              onClick={() => {
                setOpenCategorySheet(false);
                setCategorySearch('');
              }}
              style={{ color: '#64748b' }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          {/* Busca interna de categoria */}
          <div style={{ padding: '8px 16px', borderBottom: '1px solid #f1f5f9' }}>
            <div className="sale-mobile-search-bar" style={{ margin: 0 }}>
              <SearchIcon style={{ color: '#64748b', fontSize: 18 }} />
              <input
                type="text"
                className="sale-mobile-search-input"
                placeholder="Filtrar categorias..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
              />
              {categorySearch && (
                <IconButton size="small" onClick={() => setCategorySearch('')} style={{ padding: 2 }}>
                  <ClearIcon style={{ fontSize: 16 }} />
                </IconButton>
              )}
            </div>
          </div>

          {/* Lista Vertical de Categorias */}
          <div className="sale-mobile-drawer-body" style={{ padding: 0 }}>
            {/* Opção Todos */}
            <div
              className={`sale-mobile-category-sheet-item ${selectedCategory === 'ALL' ? 'selected' : ''}`}
              onClick={() => {
                setSelectedCategory('ALL');
                setOpenCategorySheet(false);
              }}
            >
              <div className="sale-mobile-category-sheet-name">
                <span>Todos os Itens</span>
              </div>
              <span className="sale-mobile-category-sheet-count">{categoriesList.totalCount}</span>
            </div>

            {filteredCategorySheetList.map((cat) => {
              const isSelected = selectedCategory === cat.id;
              return (
                <div
                  key={cat.id}
                  className={`sale-mobile-category-sheet-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setOpenCategorySheet(false);
                  }}
                >
                  <div className="sale-mobile-category-sheet-name">
                    <span>{cat.name}</span>
                    {isSelected && <CheckIcon style={{ color: '#17497f', fontSize: 18 }} />}
                  </div>
                  <span className="sale-mobile-category-sheet-count">{cat.count} itens</span>
                </div>
              );
            })}
          </div>
        </div>
      </Drawer>

      {/* Lista Compacta de Itens com Infinite Scroll */}
      <div className="sale-mobile-items-list" onScroll={handleScroll}>
        {isLoading && allCatalogItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <CircularProgress size={28} style={{ color: '#17497f' }} />
            <div style={{ marginTop: 8, color: '#64748b', fontSize: '0.82rem' }}>
              Carregando itens...
            </div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 16px', color: '#64748b' }}>
            <SearchIcon style={{ fontSize: 36, color: '#94a3b8', marginBottom: 6 }} />
            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
              Nenhum item encontrado
            </div>
            <div style={{ fontSize: '0.78rem', marginTop: 2 }}>
              Tente buscar com outro termo ou categoria.
            </div>
          </div>
        ) : (
          <>
            {visibleItems.map((item) => {
              const cartEntry = cartMap.get(item.itemKey);
              const isInCart = Boolean(cartEntry && cartEntry.amount > 0);

              return (
                <div
                  key={item.itemKey}
                  className={`sale-mobile-row ${isInCart ? 'in-cart' : ''}`}
                >
                  <div className="sale-mobile-row-info">
                    <div className="sale-mobile-row-name" title={item.name}>
                      {item.name}
                    </div>
                    <div className="sale-mobile-row-meta">
                      <span className="sale-mobile-row-price">
                        {formattedCurrency(item.numericValue)}
                      </span>
                      <span className="sale-mobile-row-tag">
                        {item.typeItem === 'service' ? 'Serviço' : item.displayCategory}
                      </span>
                    </div>
                  </div>

                  {isInCart ? (
                    <div className="sale-mobile-row-stepper">
                      <button
                        type="button"
                        className="sale-mobile-step-btn"
                        onClick={() => handleDecrement(item, cartEntry)}
                        aria-label="Diminuir"
                      >
                        <RemoveIcon style={{ fontSize: 13 }} />
                      </button>
                      <span
                        className="sale-mobile-step-num"
                        onClick={() => handleOpenQuantityModal(item, cartEntry)}
                        title="Toque para definir quantidade fracionada"
                      >
                        {Number(cartEntry.amount).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                      </span>
                      <button
                        type="button"
                        className="sale-mobile-step-btn"
                        onClick={() => handleIncrement(item, cartEntry)}
                        aria-label="Aumentar"
                      >
                        <AddIcon style={{ fontSize: 13 }} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="sale-mobile-btn-add"
                      onClick={() => handleAddClick(item)}
                    >
                      <AddIcon style={{ fontSize: 14 }} />
                      Adicionar
                    </button>
                  )}
                </div>
              );
            })}

            {visibleLimit < filteredItems.length && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '12px 0',
                  color: '#64748b',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                }}
              >
                Exibindo {visibleItems.length} de {filteredItems.length} itens (Role para carregar mais)
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Quantidade Fracionada */}
      <SaleMobileQuantityModal
        open={Boolean(quantityModalItem)}
        onClose={() => setQuantityModalItem(null)}
        item={quantityModalItem?.item}
        currentAmount={quantityModalItem?.currentAmount || 1}
        onConfirm={handleSaveQuantity}
        onRemove={handleRemoveFromModal}
      />

      <Snackbar
        open={Boolean(stockAlert)}
        autoHideDuration={4000}
        onClose={() => setStockAlert(null)}
        style={{ zIndex: 99999999 }}
      >
        <Alert onClose={() => setStockAlert(null)} severity="error" elevation={6} variant="filled">
          {stockAlert}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaleMobileCatalog;
